import { useState, useRef, useEffect, useMemo } from "react";
import { BrandDetails } from "../types/add-brand-types";
import { ContentFocus } from "../types/creator-studio";

export const useContentGeneration = (
  brandDetails: BrandDetails,
  onUserSelectedKnowledge: (knowledge: string[]) => void,
  contentFocus: ContentFocus,
  selectedTone: string,
  setSelectedTone: (tone: string) => void
) => {
  const [activeTab, setActiveTab] = useState<"social" | "email">("social");
  const [showToneDropdown, setShowToneDropdown] = useState(false);
  const [showKnowledgeDropdown, setShowKnowledgeDropdown] = useState(false);
  const [selectedKnowledge, setSelectedKnowledge] = useState<string[]>([]);

  const toneRef = useRef<HTMLDivElement>(null);
  const knowledgeRef = useRef<HTMLDivElement>(null);

  const tones = useMemo(() => {
    return [
      "Professional",
      "Casual",
      "Friendly",
      "Witty",
      "Urgent",
      "Persuasive",
      "Empathetic",
      "Authoritative",
    ];
  }, []);

  const knowledgeFiles = useMemo(() => {
    const knowledgeBase = brandDetails.knowledge_base;
    return Object.values(knowledgeBase).map(
      (knowledge) => knowledge.actual_name
    );
  }, [brandDetails.knowledge_base]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toneRef.current && !toneRef.current.contains(event.target as Node)) {
        setShowToneDropdown(false);
      }
      if (
        knowledgeRef.current &&
        !knowledgeRef.current.contains(event.target as Node)
      ) {
        setShowKnowledgeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleKnowledge = (file: string) => {
    setSelectedKnowledge((prev) =>
      prev.includes(file) ? prev.filter((f) => f !== file) : [...prev, file]
    );
  };

  useEffect(() => {
    onUserSelectedKnowledge(selectedKnowledge);
  }, [selectedKnowledge, onUserSelectedKnowledge]);

  useEffect(() => {
    if (contentFocus !== ContentFocus.GENERAL_POST) {
      return;
    }

    setSelectedKnowledge([]);
    setShowKnowledgeDropdown(false);
  }, [contentFocus]);

  return {
    activeTab,
    setActiveTab,
    showToneDropdown,
    showKnowledgeDropdown,
    selectedTone,
    setSelectedTone,
    toneRef,
    knowledgeRef,
    setShowToneDropdown,
    setShowKnowledgeDropdown,
    tones,
    knowledgeFiles,
    selectedKnowledge,
    toggleKnowledge,
  };
};
